import React, { useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { ChevronDown, GraduationCap } from "lucide-react";
import { useHistory } from "react-router-dom";

const DEFAULT_ITEMS = [
  { label: "Medicina Interna", percentage: 30 },
  { label: "Cirugia General", percentage: 20 },
  { label: "Pediatria", percentage: 80 },
  { label: "Ginecologia y Obstetricia", percentage: 75 },
];

const BAR_COLORS = ["#7c3aed", "#2563eb", "#22c55e", "#f97316", "#ef4444", "#64748b"];

function normalizePercentage(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return 0;
  return Math.max(0, Math.min(100, Math.round(num)));
}

function getBarColor(index) {
  return BAR_COLORS[index % BAR_COLORS.length];
}

function formatRatio(correct, total, percentage) {
  const correctValue = Number(correct);
  const totalValue = Number(total);

  if (Number.isFinite(correctValue) && Number.isFinite(totalValue) && totalValue >= 0) {
    return `${correctValue}/${totalValue} (${percentage}%)`;
  }

  return `${percentage}%`;
}

export default function CardDesempenoEspecialidad({
  title = "Promedio por sección",
  actionLabel = "Ver todo",
  onAction,
  items,
  resultsPayload,
  loading = false,
}) {
  const history = useHistory();
  const [expandedSection, setExpandedSection] = useState(false);

  const sourceItems =
    items ??
    resultsPayload?.performance_by_specialty ??
    resultsPayload?.strengths ??
    DEFAULT_ITEMS;

  const itemsList = Array.isArray(sourceItems) ? sourceItems : [];
  const hasSpecialtySection = itemsList.some((item) => item?.specialty_section || item?.section);
  const handleAction = onAction || (() => history.push("/admin/especialidades"));

  const rows = hasSpecialtySection
    ? Object.values(
        itemsList.reduce((acc, item) => {
          const section = item?.specialty_section || item?.section || "Sin sección";
          const correct = Number(item?.correct_answers);
          const total = Number(item?.total_questions);
          const score = Number(item?.percentage ?? item?.score_percentage ?? item?.value ?? 0);

          if (!acc[section]) {
            acc[section] = {
              label: section,
              correct: 0,
              total: 0,
              percentageSum: 0,
              percentageCount: 0,
              children: [],
            };
          }

          if (Number.isFinite(correct)) {
            acc[section].correct += correct;
          }
          if (Number.isFinite(total)) {
            acc[section].total += total;
          }
          if (Number.isFinite(score)) {
            acc[section].percentageSum += score;
            acc[section].percentageCount += 1;
          }

          acc[section].children.push({
            label: item?.specialty_name || item?.name || item?.label || "Sin nombre",
            percentage: normalizePercentage(score),
            correct: Number.isFinite(correct) ? correct : undefined,
            total: Number.isFinite(total) ? total : undefined,
          });

          return acc;
        }, {})
      ).map((row) => {
        const hasTotal = Number.isFinite(row.total) && row.total > 0;
        const percentage = hasTotal
          ? normalizePercentage((row.correct / row.total) * 100)
          : normalizePercentage(
              row.percentageCount > 0 ? row.percentageSum / row.percentageCount : 0
            );

        return {
          label: row.label,
          percentage,
          correct: hasTotal ? row.correct : undefined,
          total: hasTotal ? row.total : undefined,
          children: row.children.sort((a, b) => b.percentage - a.percentage),
        };
      })
    : itemsList.map((item) => {
        const percentage = normalizePercentage(
          item?.percentage ?? item?.score_percentage ?? item?.value ?? 0
        );

        return {
          label: item?.specialty_name || item?.name || item?.label || "Sin nombre",
          percentage,
          correct: item?.correct_answers,
          total: item?.total_questions,
        };
      });

  return (
    <div
      className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 border border-blueGray-100 rounded-xl"
      style={{ boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)" }}
    >
      <div className="border-b border-blueGray-100 px-6 pt-6 pb-4 sm:px-7">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <GraduationCap size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold leading-tight text-blueGray-900">{title}</h3>
              <p className="mt-0.5 text-xs text-blueGray-400">Identifica tus fortalezas y áreas de oportunidad</p>
            </div>
          </div>
          <button
            className="flex-shrink-0 bg-transparent p-0 text-xs font-semibold text-indigo-500 transition-colors duration-150 hover:text-indigo-600 focus:outline-none"
            type="button"
            onClick={handleAction}
          >
            {actionLabel}
          </button>
        </div>
      </div>

      <div className="px-6 pb-6 sm:px-7">
        {loading ? (
          <div className="pt-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={`skeleton-row-${index}`} className="mb-6 last:mb-0">
                <div className="sm:flex sm:items-center">
                  <div className="sm:w-40 sm:flex-shrink-0 sm:pr-5">
                    <Skeleton variant="text" width="80%" height={28} animation="wave" />
                  </div>
                  <div className="mt-2 flex items-center sm:mt-0 sm:flex-1">
                    <div className="flex-1">
                      <Skeleton variant="rounded" width="100%" height={10} animation="wave" />
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <Skeleton variant="text" width={36} height={28} animation="wave" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm font-medium text-blueGray-400">Sin datos disponibles</div>
        ) : (
          <div className="pt-2">
            {rows.map((row, index) => {
              const barColor = getBarColor(index);
              const hasChildren = Array.isArray(row.children) && row.children.length > 0;

              const rowContent = (
                <div
                  className="grid min-w-0 flex-1 items-center gap-3"
                  style={{ gridTemplateColumns: "minmax(0, 1fr)", rowGap: "0.25rem" }}
                >
                  <div className="min-w-0 text-sm font-semibold leading-6 text-blueGray-700 sm:pr-3">
                    <span className="block overflow-hidden text-ellipsis whitespace-nowrap">{row.label}</span>
                  </div>
                  <div
                    className="flex items-center"
                    style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", columnGap: "0.75rem" }}
                  >
                    <div className="relative overflow-hidden rounded-full bg-blueGray-100" style={{ height: "10px", width: "100%" }}>
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${row.percentage}%`, backgroundColor: barColor }}
                      />
                    </div>
                    <div className="flex-shrink-0 whitespace-nowrap text-right text-sm font-semibold leading-6 text-blueGray-900">
                      {formatRatio(row.correct, row.total, row.percentage)}
                    </div>
                  </div>
                </div>
              );

              if (!hasChildren) {
                return (
                  <div
                    key={`${row.label}-${index}`}
                    className="-mx-2 mb-0.5 rounded-xl px-2 py-1.5 transition-colors last:mb-0 hover:bg-blueGray-50"
                  >
                    {rowContent}
                  </div>
                );
              }

              return (
                <Accordion
                  key={`${row.label}-${index}`}
                  disableGutters
                  square
                  elevation={0}
                  expanded={expandedSection === row.label}
                  onChange={(_, isExpanded) => setExpandedSection(isExpanded ? row.label : false)}
                  sx={{
                    mb: 0.25,
                    "&:last-of-type": { mb: 0 },
                    "&:before": { display: "none" },
                    backgroundColor: "transparent",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown size={18} className="text-blueGray-400" />}
                    sx={{
                      pl: 1,
                      pr: 0,
                      ml: -1,
                      mr: -2,
                      borderRadius: "12px",
                      minHeight: 0,
                      outline: "none",
                      alignItems: "flex-start",
                      transition: "background-color 150ms ease",
                      "&:hover": { backgroundColor: "#f8fafc" },
                      "&:focus, &:focus-visible": { outline: "none" },
                      "&.Mui-expanded": { minHeight: 0 },
                      "& .MuiAccordionSummary-content": { margin: "6px 0", minWidth: 0 },
                      "& .MuiAccordionSummary-content.Mui-expanded": { margin: "6px 0" },
                      "& .MuiAccordionSummary-expandIconWrapper": { marginTop: "9px" },
                    }}
                  >
                    {rowContent}
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
                    <div className="space-y-1.5 border-l-2 border-blueGray-100 pl-4">
                      {row.children.map((child, childIndex) => {
                        return (
                          <div
                            key={`${row.label}-${child.label}-${childIndex}`}
                            className="grid items-center gap-3"
                            style={{ gridTemplateColumns: "minmax(0, 1fr)", rowGap: "0.2rem" }}
                          >
                            <div className="min-w-0 text-xs font-medium leading-5 text-blueGray-500 sm:pr-3">
                              <span className="block overflow-hidden text-ellipsis whitespace-nowrap">{child.label}</span>
                            </div>
                            <div
                              className="flex items-center"
                              style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", columnGap: "0.75rem" }}
                            >
                              <div className="relative overflow-hidden rounded-full bg-blueGray-100" style={{ height: "8px", width: "100%" }}>
                                <div
                                  className="h-full rounded-full transition-all duration-300"
                                  style={{ width: `${child.percentage}%`, backgroundColor: barColor }}
                                />
                              </div>
                              <div className="flex-shrink-0 whitespace-nowrap text-right text-xs font-semibold leading-5 text-blueGray-700">
                                {formatRatio(child.correct, child.total, child.percentage)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
